// @ts-strict-ignore
import copy from 'clipboard-copy';
import {AttrsSchema, Component} from 'panel';
import cloneDeep from 'lodash/cloneDeep';
import findKey from 'lodash/findKey';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import {applyPatches} from 'immer';

import {spans} from 'common/util/tracing';
import {
  DOWNLOAD_FAIL_TOAST,
  DOWNLOAD_SUCCESS_TOAST,
  DOWNLOAD_TOAST_OVERLAP_MS,
  DownloadMode,
  LocalStorageKey,
  ProjectFeatureFlag,
  ReportName,
} from 'common/report/constants';
import {Bookmark, BookmarkParamsUnionType, DisplayBookmark, TimeFilter} from 'common/report/dashboards/types';
import {queryApiUrl} from 'common/report/queries/utils';
import {ReportManager} from 'common/report/report-manager';
import {UserLocalStorageHelper} from 'common/report/user-local-storage';
import {ReportQueryBuilderEventHandlers, ReportQueryBuilderParams} from 'common/report/report-manager/query-builder';
import {DashboardBookmarkOverrides, DashboardOrigin} from 'common/report/util/bookmark-overrides';
import {
  ReportHeaderEventHandlers,
  ReportHeaderParams,
  ReportHeaderProps,
} from 'common/report/report-manager/report-header';
import {
  ReportVisualizationAttrs,
  ReportVisualizationProps,
  VisualizationEventHandlers,
} from 'common/report/report-manager/visualization';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {getLocation} from 'common/util/url';
import {ReportStatus} from 'common/widgets/warning-hero/constants';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {ShowQuerySamplingCTADelay} from 'common/widgets/warning-hero/query-sampling-cta/constants';
import {emitInfraTrackEvent, emitTrackEvent, KEY_VALUES} from 'common/util/dom';
import {diffFromNow} from 'common/util/timing';
import {getBookmarkParamsWithOverrides} from 'common/report/dashboards/bookmark-overrides';
import {reportManagerFromType, submitOdaRequest, SUPPORTED_BOOKMARK_TYPES} from 'common/report/report-manager/util';
import {
  commonReportUtils,
  downloadBlobAsFile,
  getDownloadMode,
  getProjectId,
  getWorkspaceId,
  isEmbedMode,
} from 'common/report/util';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {useStore} from 'common/util/component-helpers';
import {dataGroupStore} from 'common/report/stores/data-groups';
import {userDetailsStore} from 'common/report/stores/user-details';
import {IronContextRegistry} from 'common/report/context-registry';
import {addToast, closeToast} from 'common/components/toast-area/toast-manager';
import {reporter} from 'common/report/error-reporting';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {BaseQueryErrorType, BaseQueryResultsType, BaseQueryResultsWithMetaType} from 'common/report/types';
import {ReportMeta} from 'common/components/download-wrapper/types';
import {PersistedDismissTooltipTour, TourName} from 'common/components/tooltip-tour/types';
import {userDismissedTheCurrentTooltipTour} from 'common/components/tooltip-tour/utils';
import {customEventsStore} from 'common/report/stores/custom-events';
import {HistoryManager} from 'common/contexts/history-manager';
import {BookmarkUpdateDetail, CsvExportAction, RequestOdaEvent} from 'common/report/report-manager/types';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {captureWorkflowTiming, QueryStartEvent} from 'common/util/workflow-timing';
import {CurrentProject, CurrentProjectResource} from 'common/report/model-resources/projects';
import {syncReportPropertyValueMappings} from 'common/report/util/mappings';
import {fetchDisplayNameData} from 'common/report/display-name-registry';
import {BookmarkURLResponse, bookmarkUrlsClient} from 'common/report/queries/app-api/bookmark-urls-client';
import {
  BookmarkURLTrackEvents,
  handleFetchBookmarkURLError,
  LoadLongURLTrackingProps,
} from 'common/report/util/bookmark-urls';
import {AdditionalContext, QueryContext, QueryContextManager} from 'common/query-context';
import {DownloadFormat, isScreenshotDownloadFormat} from 'common/report/util/report-download';
import {DownloadReportRequest} from 'common/types/schema4api/webapp/reports/types';
import {makeDownloadQuery} from 'common/report/queries/app-api/download-client';
import {ReportTrackingEvent} from 'common/report/report-manager/tracking';
import {
  AxisUnitOptions,
  BookmarkXAxisOptions,
  BookmarkYAxisOptions,
  InsightsQueryError,
  ShowClause,
} from 'common/report/insights/util/types';
import {DashboardCardId} from 'common/report/util/saved-entities/types';
import {updateFavicon} from 'common/util/favicon';
import {trackVisitedReport, VisitedReportReason} from 'common/util/server-tracking';
import {SHOW_MIXPANEL_TIPS_DELAY} from 'common/constants';
import {customPropertiesStore} from 'common/report/stores/custom-properties';
import {lookupTablesStore} from 'common/report/stores/lookup-tables';
import {eventDefinitionsStore} from 'common/report/stores/event-definitions';
import {
  peoplePropertyDefinitionsStore,
  propertyDefinitionsForEventsStore,
} from 'common/report/stores/property-definitions';
import {displayOptionsMatchAxis, isBehaviorShowClause} from 'common/report/insights/models/utils';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {
  BackToBoardDetail,
  BackToBoardEvent,
  BackToBoardSource,
  LoadingResource,
  ReportEditorActivePanel,
} from 'common/report-editor/types';
import {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {abbreviateNumber} from 'common/util';
import {ToastType} from 'common/components/toast/types';
import {Toast} from 'common/components/toast-area';
import {isChartCustomizationEnabled} from 'common/query-builder/chart-customization/utils';
import {bookmarkUpdatesFromChartCustomization} from 'common/report/insights/visualization/util';
import {
  ChartCustomizationUpdate,
  ChartCustomizationUpdateType,
} from 'common/query-builder/chart-customization/axis/types';
import {ReportInfo} from 'common/report/contexts/report-info';
import {getThemeTrackingProps} from 'common/query-builder/chart-customization/colors/utils';
import * as BookmarkTypes from 'common/types/reports/bookmark';
import {keyEventSwitcher} from 'common/util/keyboard';
import {StackableComponent} from 'common/components/base/stackable';
import {prompt as promptConfirmModal} from 'common/components/confirm';
import {THEME} from 'common/components/confirm/types';
import {getEditCapability} from 'common/report/util/saved-entities';
import {ApplyParamsFunction, ReportBookmarkManager} from 'common/report/insights/contexts/report-bookmark-manager';
import {InsightsBookmarkParams} from 'common/types/reports/bookmark';
import {serializeBookmark} from 'common/report/insights/contexts/utils/bookmark';

import {ReportSwitchEvent} from '../query-builder/query-builder/types';
import './index.styl';
import {template} from './template';

const EDITOR_EVENT_PREFIX = `fastedit`;
enum EditorEventNames {
  HeaderReady = `Header ready`,
  VizReady = `Viz ready`,
  UIReady = `UI ready`,
}

type ReportEditorParams = {
  // the 'originalBookmark' prop holds what's saved on the dashboard;
  // the 'editedBookmark' state entry is the current (unsaved)
  // state of the report
  originalBookmark?: Bookmark;
  editedBookmark?: DisplayBookmark;
  resultsPromise?: Promise<BaseQueryResultsType | BaseQueryErrorType>;
  setResultsInDashboardCallback?: (results: BaseQueryResultsType, useQueryApiCache?: boolean) => void;
  dashboardOverrides?: DashboardBookmarkOverrides;
  dashboardCardId?: DashboardCardId;
  bookmarkURLSlug?: string;
  lastReportQueryContext?: QueryContext;
  longUrlCount?: number;
  defaultBookmarkType?: BookmarkType;
  isFasteditPageload?: boolean;
};

type ReportEditorProps = {
  editorParams: ReportEditorParams;
};

type ReportEditorAttrs = {
  loading?: boolean;
  'is-saving-bookmark'?: boolean;
  'origin-dashboard'?: DashboardOrigin;
  'app-mode'?: boolean;

  'dashboards-route'?: DashboardOrigin[];

  // workaround for standalone apps that only persist query sampling in the url
  'enable-query-sampling'?: boolean;
};

type ReportEditorState = {
  activePanel: ReportEditorActivePanel;
  bookmarkURLSlug: string | null;
  defaultBookmarkType: BookmarkType;
  editedBookmark: DisplayBookmark;
  error: BaseQueryErrorType;
  hasReportHitUxSegmentationLimit: boolean;
  isSavingBookmark: boolean;
  loading?: Record<LoadingResource, boolean>;
  maxExportCsvLimit: number;
  originDashboard: DashboardOrigin;
  originalBookmark: Bookmark;
  querySamplingEnabled: boolean;
  renderStage: number;
  reportManager: ReportManager;
  reportSamplingFactor: number;
  results: BaseQueryResultsType;
  showCustomAlertModal?: boolean;
  showMpTips: boolean;
  showQuerySamplingCTA: boolean;
  triggerQuerySamplingTour: boolean;
  userSidebarOpen: boolean;
  uxSegmentationLimit: number;
};

export type SaveEventDetails<BookmarkParamsType extends BookmarkParamsUnionType = TODO> = {
  editedBookmark: DisplayBookmark<BookmarkParamsType>;
  hydratedBookmark?: DisplayBookmark<BookmarkParamsType>;
  originalBookmark?: Bookmark<BookmarkParamsType>;
  queryPromise?: Promise<BaseQueryResultsType | BaseQueryErrorType> | null;
  dashboard?: DashboardOrigin;
  originDashboard?: DashboardOrigin;
  saveAsNew?: boolean;
  dashboardCardId?: DashboardCardId;
  updateBookmarkUrlOnFasteditSave?: InstanceType<typeof ReportEditor>[`updateBookmarkUrlOnFasteditSave`];
  trackingProps?: Obj;
};

export type SaveEvent<BookmarkParamsType extends BookmarkParamsUnionType = TODO> = CustomEvent<
  SaveEventDetails<BookmarkParamsType>
>;

export type DeleteEvent = CustomEvent<{
  dashboardCardId?: DashboardCardId;
  originalBookmark: Bookmark;
}>;

export type UpdateEvent = CustomEvent<{
  bookmark: DisplayBookmark;
  bookmarkURLSlug?: string;
  updatePromise?: Promise<BookmarkURLResponse | DisplayBookmark>;
  isReportSwitch?: boolean;
  savedBehaviorId?: number;
}>;

export type ReportEditorJsxProps = {
  attrs?: ReportEditorAttrs;
  props: ReportEditorProps;
  on?: {
    'back-to-board'?: (ev: BackToBoardEvent) => void;
    save: (ev: SaveEvent) => void;
    update: (ev: UpdateEvent) => void;
    delete?: (ev: DeleteEvent) => void;
    move: (ev: CustomEvent) => void;
    'toggle-query-sampling'?: (ev: CustomEvent<{on: boolean}>) => void;
  };
};

export const BookmarkTypeToReportName: Partial<Record<BookmarkType, ReportName>> = {
  [BookmarkType.Flows]: ReportName.Flows,
  [BookmarkType.Funnels]: ReportName.Funnels,
  [BookmarkType.Insights]: ReportName.Insights,
  [BookmarkType.Retention]: ReportName.Retention,
};

export const getChartCustomizationUpdateTrackingProps = (
  updateType: ChartCustomizationUpdateType,
  diff?: Partial<BookmarkYAxisOptions>,
  old?: BookmarkYAxisOptions,
) => {
  const trackingProps = {'update type': updateType};

  let subcomponentUpdateType: keyof BookmarkYAxisOptions | keyof AxisUnitOptions | null = null;

  if (diff?.title?.text !== undefined) {
    trackingProps[`title`] = {before: old?.title?.text, after: diff?.title?.text};
    subcomponentUpdateType = `title`;
  }

  const topLevelOptions: Array<keyof BookmarkYAxisOptions> = [`type`, `min`, `max`];
  topLevelOptions.forEach((option) => {
    if (diff?.[option] !== undefined) {
      trackingProps[option] = {
        before: old?.[option] ?? null,
        after: diff?.[option],
      };
      subcomponentUpdateType = option;
    }
  });

  const axisUnitOptions: Array<keyof AxisUnitOptions> = [`prefix`, `suffix`, `precision`];
  axisUnitOptions.forEach((option) => {
    if (diff?.axisUnit?.[option] !== undefined) {
      trackingProps[option] = {
        before: old?.axisUnit?.[option],
        after: diff?.axisUnit?.[option],
      };
      subcomponentUpdateType = option;
    }
  });

  return {...trackingProps, 'subcomponent update type': subcomponentUpdateType};
};

const getChartCustomizationSaveYAxisTrackingProps = (
  axisAssignment: BookmarkTypes.AxisAssignment,
  showSections: ShowClause[],
  setting?: BookmarkYAxisOptions,
) => {
  const numMetrics = showSections.filter((x) => displayOptionsMatchAxis(x.display, axisAssignment)).length;
  return {
    visible: setting?.labels?.enabled,
    label: setting?.title?.text,
    'unit prefix': setting?.axisUnit?.prefix,
    'unit suffix': setting?.axisUnit?.suffix,
    min: setting?.min,
    max: setting?.max,
    decimals: setting?.axisUnit?.precision,
    scale: setting?.type,
    numMetrics,
  };
};

const getChartCustomizationSaveXAxisTrackingProps = (setting?: BookmarkXAxisOptions) => {
  return {
    visible: setting?.labels?.enabled,
    label: setting?.title?.text,
  };
};

function getBookmarkTypeFromReportName(reportName: ReportName): BookmarkType {
  return findKey(BookmarkTypeToReportName, (value) => value === reportName) as BookmarkType;
}

function isErrorResult(results: BaseQueryResultsType | BaseQueryErrorType): results is BaseQueryErrorType {
  return `error` in results && !!results.error;
}

export class ReportEditor extends Component<ReportEditorState, ReportEditorAttrs, any, any, IronContextRegistry> {
  // resultsPromise is passed via prop, internalResultsPromise is maintained on this component when it issues a query
  public resultsPromise: Promise<BaseQueryResultsType | BaseQueryErrorType> = null;
  private internalResultsPromise: Promise<BaseQueryResultsType | BaseQueryErrorType> = null;

  private declare dashboardOverrides: DashboardBookmarkOverrides;
  // Needs to be persisted to distinguish between original report (e.g. `report-123`) and
  // linked report (e.g. `report-link-123`)
  private declare dashboardCardId: DashboardCardId;
  private declare setResultsInDashboardCallback?: (results: BaseQueryResultsType, useQueryApiCache?: boolean) => void;
  private queryCount = 0;
  private lastReportQueryContext: QueryContext = null;
  private longUrlCount = 0;
  private isFasteditPageload = false;

  private resolveCurrentProject = null;
  private currentProjectPromise = new Promise((resolve) => {
    this.resolveCurrentProject = resolve;
  });

  private resolveEditedBookmark = null;
  private editedBookmarkPromise = new Promise((resolve) => {
    this.resolveEditedBookmark = resolve;
  });

  private declare userLocalStorageDismissTipsHelper: UserLocalStorageHelper<PersistedDismissTooltipTour>;

  static get attrsSchema(): AttrsSchema<ReportEditorAttrs> {
    return {
      ...super.attrsSchema,
      loading: {
        type: `boolean`,
        description:
          `When viewing a saved report, the parent component is responsible for fetching/passing the` +
          ` saved report to mp-report-editor. This flag indicates if the parent is still loading the` +
          ` saved report. When viewing a bookmark url slug, mp-report-editor is responsible for loading` +
          ` the bookmark params for the slug, in which case this flag will be false.`,
      },
      'is-saving-bookmark': {
        type: `boolean`,
        description: `Is bookmark currently being saved?`,
      },
      'origin-dashboard': `json`,
      'dashboards-route': `json`,
      'app-mode': `boolean`,
      'enable-query-sampling': `boolean`,
    };
  }

  get config() {
    return {
      contexts: [
        `urlManager` as const,
        `historyManager` as const,
        `reportLayoutManager` as const,
        `reportInfo` as const,
        `currentProject` as const,
        `queryContextManager` as const,
        `localStorageManager` as const,
        `metrics` as const,
        `behaviors` as const,
        `i18n` as const,
        `reportBookmark` as const,
      ],
      defaultContexts: {
        historyManager: new HistoryManager(),
        queryContextManager: new QueryContextManager(),
        metrics: new MetricsResource(),
        behaviors: new BehaviorsResource(),
        reportInfo: new ReportInfo(),
        reportBookmark: new ReportBookmarkManager(),
      },
      hooks: {
        /**
         * State management for the bookmark report-editor is working with is unwieldy.
         * This preUpdate hook makes sure state and the state inside `reportBookmark` are always in sync.
         *
         * @todo(metrics) remove state.editedBookmark from this component and consume from the reportBookmark context.
         * For future React migrations, this will become a regular React context.
         */
        preUpdate: (state: ReportEditorState) => {
          if (state?.editedBookmark) {
            state.editedBookmark = this.syncReportBookmarkStore(state.editedBookmark);
            Object.assign(this.state, state);
          }
        },
      },
      defaultState: {
        activePanel: ReportEditorActivePanel.QueryBuilder,
        defaultBookmarkType: null,
        loading: {
          [LoadingResource.Result]: true,
          [LoadingResource.ApiResources]: true,
        },
        editedBookmark: null,
        originalBookmark: null,
        originDashboard: null,
        bookmarkURLSlug: null,
        maxExportCsvLimit: 10000,
        hasReportHitUxSegmentationLimit: false,
        renderStage: 0,
        results: null,
        error: null,
        reportManager: null,
        reportSamplingFactor: 1,
        querySamplingEnabled: false,
        showQuerySamplingCTA: false,
        showMpTips: false,
        triggerQuerySamplingTour: false,
        userSidebarOpen: false,
        uxSegmentationLimit: 1000,
        isSavingBookmark: false,
      },
      helpers: {
        incrRenderStage: () => this.update({renderStage: this.state.renderStage + 1}),
        headerInserted: () => {
          this.helpers.incrRenderStage();
          requestAnimationFrame(() => {
            emitInfraTrackEvent(window, {
              eventName: EditorEventNames.HeaderReady,
              eventPrefix: EDITOR_EVENT_PREFIX,
              props: {
                elapsedMs: diffFromNow(this.timings.initializingStartedAt),
              },
            });
          });
        },
        vizInserted: () => {
          this.helpers.incrRenderStage();
          requestAnimationFrame(() => {
            emitInfraTrackEvent(window, {
              eventName: EditorEventNames.VizReady,
              eventPrefix: EDITOR_EVENT_PREFIX,
              props: {
                elapsedMs: diffFromNow(this.timings.initializingStartedAt),
              },
            });
          });
        },
        qbInserted: () => {
          this.helpers.incrRenderStage();
          requestAnimationFrame(() => {
            emitInfraTrackEvent(window, {
              eventName: EditorEventNames.UIReady,
              eventPrefix: EDITOR_EVENT_PREFIX,
              props: {
                elapsedMs: diffFromNow(this.timings.initializingStartedAt),
              },
            });
          });
        },
        hasMultipleMetrics: () => {
          return this.state.editedBookmark?.params?.sections?.show?.length > 1;
        },
      },
      template,
    };
  }

  get t() {
    return this.getContext(`i18n`).t;
  }

  get historyManager() {
    return this.getContext(`historyManager`);
  }

  setupUndoRedo() {
    this.historyManager.setup((changes, metadata) => {
      if (metadata.key === `header`) {
        const editedBookmark = applyPatches(this.state.editedBookmark, changes);
        this.update({editedBookmark});
        this.dispatchUpdate();
      } else {
        const params = applyPatches(this.state.editedBookmark.params, changes);
        this.updateBookmarkParams({
          params,
          queryReason: QueryReason.QbOtherUpdate,
          requery: metadata?.requery ?? true,
          hasChangedChartType: metadata?.hasChangedChartType ?? false,
        });
      }
    });
  }

  confirmBackToBoard = async (): Promise<boolean> => {
    if (this.reportHasUnsavedChanges()) {
      const promptResponse = await promptConfirmModal({
        theme: THEME.DELETE,
        title: this.t(`reports.unsaved-changes.header`),
        description: this.t(`reports.unsaved-changes.description`),
        'confirm-button-label': this.t(`common.close`),
      });

      return promptResponse.didConfirm;
    }

    return true;
  };

  handleEscapeKeyPress = async () => {
    if (this.attr(`app-mode`) || StackableComponent.getCurrentStackingContextDepth() !== 0) {
      return;
    }

    const backToBoard = await this.confirmBackToBoard();
    if (!backToBoard) {
      return;
    }

    const boardToBoardEvent: BackToBoardEvent = new CustomEvent(`back-to-board`, {
      detail: {
        source: BackToBoardSource.EscapeKey,
      },
    });
    this.dispatchEvent(boardToBoardEvent);
  };

  onKeyPress = (ev: KeyboardEvent) => {
    keyEventSwitcher(
      {
        [KEY_VALUES.ESCAPE]: this.handleEscapeKeyPress,
      },
      {stopPropagation: true},
    )(ev);
  };

  connectedCallback() {
    super.connectedCallback();

    this.userLocalStorageDismissTipsHelper = new UserLocalStorageHelper({
      context: this.getContext(`localStorageManager`),
      namespace: LocalStorageKey.DismissTooltipTour,
    });

    if (this.currentProjectContext) {
      this.resolveCurrentProject();
    }

    this.captureFasteditWorkflows();
    this.trackFasteditVisitedReport();

    if (!this.attr(`loading`) && !this.state.bookmarkURLSlug) {
      // Loading a blank report or old long url report (pre bookmark urls)
      this.helpers.incrRenderStage();
    }

    this.syncDisplayNames();
    useStore(this, userDetailsStore).fetch(); // needed for entity permissions
    useStore(this, dataGroupStore).fetch({
      projectId: this.projectId,
      workspaceId: this.workspaceId,
    });
    useStore(this, customEventsStore).fetch({
      projectId: this.projectId,
      workspaceId: this.workspaceId,
    });

    const editedBookmark = this.state.editedBookmark;
    const originalBookmark = this.state.originalBookmark;
    if (editedBookmark || originalBookmark) {
      const bookmarkType = editedBookmark?.type ?? originalBookmark?.type;
      const insightsReportMode = bookmarkType;
      this.setReportManager({bookmarkType, insightsReportMode});
      this.updateReportNameOverride();
    }

    this.setupUndoRedo();
    this.reportBookmark.setup(this.reportBookmarkStoreApplyFn, {
      metrics: this.getContext(`metrics`),
      behaviors: this.getContext(`behaviors`),
      i18n: this.getContext(`i18n`),
      currentProject: this.getContext(`currentProject`),
    });

    document.addEventListener(`keydown`, this.onKeyPress);
  }

  resourceCustomSubscriptions = new Map([[CurrentProjectResource, () => this.resolveCurrentProject()]]);

  private async syncDisplayNames() {
    // the promises for these functions are stored to ensure requests are ran only once.
    // therefore, can safely call this multiple times.
    const propValMappingsPromise = syncReportPropertyValueMappings(this.projectId);
    const displayNameDataPromise = fetchDisplayNameData(this.projectId, this.workspaceId);
    await Promise.all([propValMappingsPromise, displayNameDataPromise]);
  }

  disconnectedCallback() {
    this.reportBookmark.teardown();
    this.historyManager.teardown();
    this.getContext(`reportInfo`).removeReportNameOverride();
    document.removeEventListener(`keydown`, this.onKeyPress);
    super.disconnectedCallback();
  }

  attributeChangedCallback(name, oldValue, newValue): void {
    super.attributeChangedCallback(name, oldValue, newValue);

    if (name === `loading`) {
      if (this.attr(`loading`)) {
        this.update({renderStage: 0});
      } else {
        this.update({renderStage: 1});
      }
    }

    if (name === `enable-query-sampling` && this.attr(`enable-query-sampling`) !== this.state.querySamplingEnabled) {
      this.handleQuerySamplingToggled(this.attr(`enable-query-sampling`));
    }

    if (name === `origin-dashboard` && this.attr(name)) {
      this.update({originDashboard: this.attr(name)});
    }
  }

  // Renamed to avoid conflict with React passing down currentProject
  get currentProjectContext(): CurrentProject {
    return this.getContext(`currentProject`).get().value;
  }

  get reportBookmark(): ReportBookmarkManager {
    return this.getContext(`reportBookmark`);
  }

  get bookmark() {
    return this.reportBookmark.bookmark;
  }

  private shouldResetManager(
    previousParams: DisplayBookmark | undefined | null,
    newParams: DisplayBookmark | undefined | null,
  ) {
    // We can't set a manager if there's no params
    if (!newParams) {
      return false;
    }

    // If there's no report manager or previous params, need to set a new one
    if (!this.state.reportManager || !previousParams) {
      return true;
    }

    return this.state.reportManager.hasUnsavedBookmarkParams(
      serializeBookmark(previousParams)?.params,
      serializeBookmark(newParams)?.params,
    );
  }

  private trackQuery({
    eventName,
    params,
    bookmarkName,
    errorMessage,
    error,
    props,
  }: {
    eventName: ReportTrackingEvent;
    params: any;
    bookmarkName?: string;
    errorMessage?: string;
    error?: BaseQueryErrorType | InsightsQueryError;
    props?: Record<string, any>;
  }) {
    const showSections = params.sections?.show || [];

    const extraProps = {
      ...(errorMessage ? {failed: true, error: errorMessage} : {}),
      'Query Sampling Enabled': this.state.querySamplingEnabled,
      'Bookmark ID': this.state.originalBookmark?.id,
      'Bookmark Name': bookmarkName ?? this.state.originalBookmark?.name,
      'Is App Mode': this.attr(`app-mode`),
      ...this.getContext(`reportLayoutManager`).getTrackingProps(),
      'Chart Customization': {
        [BookmarkTypes.AxisAssignment.Primary]: getChartCustomizationSaveYAxisTrackingProps(
          BookmarkTypes.AxisAssignment.Primary,
          showSections,
          params.displayOptions?.primaryYAxisOptions,
        ),
        [BookmarkTypes.AxisAssignment.Secondary]: getChartCustomizationSaveYAxisTrackingProps(
          BookmarkTypes.AxisAssignment.Secondary,
          showSections,
          params.displayOptions?.secondaryYAxisOptions,
        ),
        [ChartCustomizationUpdateType.XAxis]: getChartCustomizationSaveXAxisTrackingProps(
          params.displayOptions?.xAxisOptions,
        ),
        'legend color overrides': Object.keys(params.legend?.overrides ?? {}).length,
        ...(params?.displayOptions?.theme ? {theme: getThemeTrackingProps(params.displayOptions.theme)} : {}),
      },
      ...props,
    };

    return this.state.reportManager.trackQuery({
      eventName,
      params,
      project: this.currentProjectContext,
      error,
      extraProps,
      availableContexts: {
        behaviors: this.getContext(`behaviors`),
        i18n: this.getContext(`i18n`),
        metrics: this.getContext(`metrics`),
      },
    });
  }

  setLoading(resource: LoadingResource, loading: boolean) {
    this.update((draft) => {
      draft.loading[resource] = loading;
      return draft;
    });
  }

  trackFasteditVisitedReport() {
    if (this.attr(`app-mode`)) {
      return;
    }

    Promise.all([this.currentProjectPromise, this.editedBookmarkPromise]).then(() => {
      const reportName = this.getReportName();

      mixpanel.track(`Viewed report`, {
        tab: reportName,
        is_volt: true,
        template_type: this.state.originDashboard?.template_type,
      });

      const additionalProps = {template_type: this.state.originDashboard?.template_type};

      if (this.hasCreateAlertIntent()) {
        additionalProps[`create alert CTA`] = true;
      }

      trackVisitedReport({
        reportName,
        projectId: getProjectId(),
        reason: VisitedReportReason.ReportModal,
        additionalProps,
      });
    });
  }

  captureFasteditWorkflows() {
    if (this.attr(`app-mode`)) {
      return;
    }

    this.editedBookmarkPromise.then(() => {
      const triggerName = this.isFasteditPageload ? `pageload` : `click card open`;
      const triggerKey = this.isFasteditPageload ? `pageload` : `click-card-open`;
      const additionalProps = () => ({bookmarkType: this.bookmarkType});

      captureWorkflowTiming(this, {
        name: `Fastedit ${triggerName} (header ready)`,
        key: `fastedit-${triggerKey}-header-ready`,
        isComplete: (ev) => ev.detail.eventName.includes(EditorEventNames.HeaderReady),
        additionalProps,
      });
      captureWorkflowTiming(this, {
        name: `Fastedit ${triggerName} (viz ready)`,
        key: `fastedit-${triggerKey}-viz-ready`,
        isComplete: (ev) => ev.detail.eventName.includes(EditorEventNames.VizReady),
        additionalProps,
      });
      captureWorkflowTiming(this, {
        name: `Fastedit ${triggerName} (UI ready)`,
        key: `fastedit-${triggerKey}-ui-ready`,
        isComplete: (ev) => ev.detail.eventName.includes(EditorEventNames.UIReady),
        additionalProps,
      });
      captureWorkflowTiming(this, {
        name: `Fastedit ${triggerName} issue query`,
        key: `fastedit-${triggerKey}-query`,
        isComplete: (ev) => ev.detail.eventName === QueryStartEvent,
        additionalProps,
      });
    });
  }

  setEditorParams({
    originalBookmark,
    editedBookmark,
    dashboardOverrides,
    dashboardCardId,
    resultsPromise,
    bookmarkURLSlug = null,
    lastReportQueryContext,
    longUrlCount = 0,
    defaultBookmarkType,
    isFasteditPageload = false,
    setResultsInDashboardCallback = null,
  }: ReportEditorParams) {
    this.update({defaultBookmarkType});
    this.isFasteditPageload = isFasteditPageload;
    this.dashboardOverrides = dashboardOverrides;

    if (longUrlCount !== this.longUrlCount) {
      this.updateParams({originalBookmark, editedBookmark, dashboardCardId, resultsPromise});

      if (this.isConnected && editedBookmark) {
        this.longUrlCount = longUrlCount;

        const trackingProps: LoadLongURLTrackingProps = {
          tab: this.getReportName(),
          'Bookmark ID': originalBookmark?.id,
          'Is Fastedit Mode': !this.attr(`app-mode`),
        };
        emitTrackEvent(this, {
          eventName: BookmarkURLTrackEvents.LoadLongURL,
          props: trackingProps,
        });

        if (this.shouldGenerateBookmarkUrl()) {
          // rewrite long URL to bookmark url slug
          this.dispatchUpdate();
        }
      }

      return;
    } else if (bookmarkURLSlug) {
      if (bookmarkURLSlug !== this.state.bookmarkURLSlug) {
        this.fetchBookmarkURL(bookmarkURLSlug).then((results) => {
          if (results) {
            this.updateParams({
              editedBookmark: {
                type: getBookmarkTypeFromReportName(results.type),
                params: results.params,
                name: results.name,
                description: results.description,
              },
              originalBookmark: results?.bookmark,
            });
            if (results.overrides?.originDashboard) {
              this.update({originDashboard: results.overrides.originDashboard});
            }
            if (!isNil(results.overrides?.querySamplingEnabled)) {
              this.update({querySamplingEnabled: results.overrides.querySamplingEnabled});
            }
            if (results.overrides?.dashboardCardId) {
              this.dashboardCardId = results.overrides?.dashboardCardId;
            }
          } else {
            // handle bookmark url not found
            const type = this.state.defaultBookmarkType;
            this.updateParams({
              editedBookmark: {
                type,
                params: this.createReportManager({
                  bookmarkType: type,
                  insightsReportMode: type,
                }).getDefaultBookmarkParams(this.currentProjectContext),
                name: ``,
                description: ``,
              },
            });
          }
        });
      }

      return;
    } else if (lastReportQueryContext && !isEqual(lastReportQueryContext, this.lastReportQueryContext)) {
      this.lastReportQueryContext = lastReportQueryContext;
      const bookmarkType = this.state.defaultBookmarkType;
      const newReportManager = this.createReportManager({bookmarkType, insightsReportMode: bookmarkType});
      const params = newReportManager.getBookmarkParamsFromQueryContext(lastReportQueryContext, {
        project: this.currentProjectContext,
        customEvents: Object.values(customEventsStore.value),
      });
      if (!isNil(lastReportQueryContext.querySamplingEnabled)) {
        this.update({querySamplingEnabled: lastReportQueryContext.querySamplingEnabled});
      }

      this.updateParams({
        editedBookmark: {
          type: bookmarkType,
          params,
          name: ``,
          description: ``,
        },
      });
      this.dispatchUpdate();
      return;
    }

    this.updateParams({
      originalBookmark,
      editedBookmark,
      dashboardCardId,
      resultsPromise,
      setResultsInDashboardCallback,
    });
  }

  set editorParams(params: ReportEditorParams) {
    this.setEditorParams(params);
  }

  updateParams({
    originalBookmark,
    editedBookmark,
    dashboardCardId,
    resultsPromise,
    setResultsInDashboardCallback = null,
  }: ReportEditorParams) {
    if (!originalBookmark && !editedBookmark) {
      return;
    }

    this.resultsPromise = resultsPromise;
    this.dashboardCardId = dashboardCardId;
    this.setResultsInDashboardCallback = setResultsInDashboardCallback;

    // Maintain previous bookmark state for requerying in case it gets overridden
    const previousEditedBookmark = this.state.editedBookmark;

    let shouldResetReportManager = false;

    if (!isEqual(originalBookmark, this.state.originalBookmark)) {
      this.update({originalBookmark});

      shouldResetReportManager = this.shouldResetManager(previousEditedBookmark, originalBookmark);

      // if original bookmark has changed, reset the edited bookmark
      if (originalBookmark) {
        if (this.dashboardOverrides) {
          this.update({
            editedBookmark: {
              ...this.state.originalBookmark,
              params: this.getBookmarkParamsWithOverrides(
                this.state.originalBookmark.params,
                this.state.originalBookmark.type,
              ),
            },
          });
        } else {
          this.update({editedBookmark: cloneDeep(originalBookmark)});
        }
      }
    }

    if (editedBookmark && !isEqual(serializeBookmark(editedBookmark), serializeBookmark(this.state.editedBookmark))) {
      // if an edited bookmark is provided, assume it is overridden.
      // this is to make sure that shared link with edited state always has the same results.
      this.update({editedBookmark});
      shouldResetReportManager = this.shouldResetManager(previousEditedBookmark, editedBookmark);
    }

    if (this.state.editedBookmark) {
      this.resolveEditedBookmark();
      if (this.isConnected) {
        this.updateReportNameOverride();
      }
    } else {
      reporter.error(`mp-report-editor: editedBookmark is not initialized`);
    }

    if (shouldResetReportManager) {
      const bookmarkType = this.state.editedBookmark.type;
      const insightsReportMode = this.state.editedBookmark.type;
      this.setReportManager({bookmarkType, insightsReportMode});
    }

    // If the bookmark is bugged we should clear to an empty state and display an error
    if (originalBookmark?.params?.error) {
      const params = this.state.reportManager.getDefaultBookmarkParams(this.currentProjectContext);
      this.update({
        error: {error: originalBookmark.error, status: 500},
        editedBookmark: {...this.state.editedBookmark, params},
      });
    }

    this.updateQueryContext();
  }

  updateReportNameOverride() {
    const prevReportName = this.getContext(`reportInfo`).getReportName();
    const currReportName = this.getReportName();
    // Only update report name in context if it changes. This is to prevent potential infinite loop.
    // Updating sidenav context will trigger re-render, which results in this method being called again.
    if (currReportName !== prevReportName) {
      this.getContext(`reportInfo`).setReportNameOverride(currReportName);
    }
  }

  updateQueryContext() {
    if (this.state.reportManager) {
      const queryContext = this.getReportQueryContext();
      this.getContext(`queryContextManager`).updateQuery(queryContext);
      if (!this.state.originDashboard) {
        // new report
        this.getContext(`queryContextManager`).updateBoardPermissions(true);
      } else if (`can_update_basic` in this.state.originDashboard) {
        // only update board permissions if permission info is available
        this.getContext(`queryContextManager`).updateBoardPermissions(
          getEditCapability(this.state.originDashboard).enableEdit,
        );
      }
    }
  }

  createReportManager({
    bookmarkType,
    insightsReportMode,
  }: {
    bookmarkType: BookmarkType;
    insightsReportMode: BookmarkType;
  }): ReportManager {
    const {pathname, search, hash} = getLocation();
    return reportManagerFromType({
      bookmarkType,
      projectId: this.projectId,
      workspaceId: this.workspaceId,
      insightsReportMode,
      metricsResource: this.getContext(`metrics`),
      behaviorsResource: this.getContext(`behaviors`),
      dashboardOverrides: this.dashboardOverrides,
      location: {
        state: ``,
        key: ``,
        pathname,
        search,
        hash,
      },
    });
  }

  setReportManager({bookmarkType, insightsReportMode}: {bookmarkType: BookmarkType; insightsReportMode: BookmarkType}) {
    if (!this.isConnected) {
      return;
    }

    const prevReportManager = this.state.reportManager;
    prevReportManager?.tracker.stopListenForTracking(this);

    this.update({reportManager: this.createReportManager({bookmarkType, insightsReportMode})});

    // reportLayoutManager is provided at a global level, so we should reset the layout to override a previously mutated layout
    this.resetReportLayout();

    if (!this.state.reportManager) {
      throw new Error(`${bookmarkType} bookmarks do not have a ReportManager yet`);
    }

    const apiResourcesPromise = this.state.reportManager.waitForApiResources({
      bookmarkParams: this.state.editedBookmark.params,
    });
    // Wait for necessary resources (saved formulas, behaviors) to be loaded before rendering query builder
    // This is needed to prevent invalid selections based on a formula's referencedMetrics
    apiResourcesPromise.then(() => {
      this.setLoading(LoadingResource.ApiResources, false);

      // makes sure the bookmark is hydrated when metrics and behaviors are loaded
      this.syncReportBookmarkStore();
    });

    this.state.reportManager.tracker.listenForTracking(this);

    // this promise is provided through a prop and can be falsey
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    if (this.resultsPromise) {
      this.internalResultsPromise = this.resultsPromise;
      this.emitQueryTrackEvent(true);
      this.internalResultsPromise.then((results) => {
        this.setLoading(LoadingResource.Result, false);
        this.handleResults(results, true);
      });
    } else {
      // Wait for necessary resources (saved formulas) to be loaded before querying. This allows
      // expanding out a saved formula's referenced metrics before querying, which e.g. prevents
      // invalid query params being sent for a saved formula that includes cohorts
      apiResourcesPromise.then(() => {
        this.query(this.state.originalBookmark ? QueryReason.BookmarkView : QueryReason.QbOtherUpdate);
      });
    }
  }

  getBookmarkParamsWithOverrides(bookmarkParams: any, bookmarkType: BookmarkType): any {
    if (!this.dashboardOverrides) {
      return bookmarkParams;
    }

    const {additionalFilters, additionalBreakdowns, chartTypeOverride, dateRangeOverride} = this.dashboardOverrides;

    // Dashboards uses TimeFilter instead of DateRange for legacy reasons as well as caching getDisplayText()
    let timeFilterOverride: TimeFilter = null;
    if (dateRangeOverride) {
      const dateRange = new DateRange(dateRangeOverride);
      timeFilterOverride = {dateRange, displayText: dateRange.getDisplayText()};
    }

    const type = bookmarkParams?.sections?.show?.some((show) => isBehaviorShowClause(show))
      ? BookmarkType.Insights
      : bookmarkType;

    return getBookmarkParamsWithOverrides({
      bookmarkParams,
      bookmarkType: type,
      chartTypeOverride,
      timeFilterOverride,
      additionalFilters: additionalFilters ? additionalFilters.map((attrs) => new PropertyFilter(attrs)) : [],
      additionalBreakdowns: additionalBreakdowns ?? [],
    });
  }

  get projectId(): number {
    return parseInt(getProjectId());
  }

  get workspaceId(): number {
    return parseInt(getWorkspaceId());
  }

  get userId(): number {
    return userCookieUtils.getUserIdentityFromCookie()?.id;
  }

  queryBuilderParams(): ReportQueryBuilderParams<any, BaseQueryResultsType, InsightsQueryError> {
    return {
      originDashboard: this.state.originDashboard,
      dashboardOverrides: this.dashboardOverrides,
      projectId: this.projectId,
      userId: this.userId,
      workspaceId: this.workspaceId,
      appMode: this.attr(`app-mode`),
      project: this.currentProjectContext,
      bookmarkParams: this.bookmark.params,
      results: this.state.results,
      error: this.state.error,
      dataGroups: Object.values(dataGroupStore.value),
      customEvents: Object.values(customEventsStore.value),
      insightsReportMode: this.state.reportManager.insightsReportMode,
    };
  }

  reportHasData() {
    return (
      !this.state.error &&
      !this.state.loading[LoadingResource.Result] &&
      this.state.results &&
      !this.state.reportManager.isEmptyResults(this.state.results, this.state.editedBookmark.params) &&
      this.state.reportManager.isQueryable(this.state.editedBookmark.params)
    );
  }

  reportHeaderParams(): ReportHeaderParams {
    const hasUnsavedChanges = this.reportHasUnsavedChanges();
    const userDimissedQuerySamplingTour = userDismissedTheCurrentTooltipTour(
      TourName.QueryTimeSampling,
      this.userLocalStorageDismissTipsHelper,
    );

    let dashboardsRoute = this.attr(`dashboards-route`);
    if (!dashboardsRoute && this.state.originalBookmark?.dashboard?.ancestors) {
      const dashboard = this.state.originalBookmark.dashboard;
      dashboardsRoute = [...dashboard.ancestors, {id: dashboard.id, title: dashboard.title}];
    }

    return {
      appMode: this.attr(`app-mode`),
      csvExportEnabled: this.state.reportManager.isCsvExportEnabled,
      fixedCsvExportLimit: this.state.reportManager.getFixedCsvExportLimit({
        bookmarkParams: this.state.editedBookmark.params,
        currentProject: this.currentProjectContext,
      }),
      hasUnsavedChanges,
      originDashboard: this.state.originDashboard,
      dashboardsRoute,
      projectId: this.projectId,
      reportHasData: this.reportHasData(),
      selectedBookmarkDashboard: this.state.originalBookmark?.dashboard,
      selectedBookmarkDescription: this.state.editedBookmark.description,
      selectedBookmarkName: this.state.editedBookmark.name,
      isSavingBookmark: this.attr(`is-saving-bookmark`),
      userId: this.userId,
      workspaceId: this.workspaceId,
      samplingFactor: this.state.reportManager.getQuerySamplingFactor(this.state.results),
      isQuerySamplingEnabled: this.state.querySamplingEnabled,
      triggerQuerySamplingTourTooltip: !userDimissedQuerySamplingTour && this.state.triggerQuerySamplingTour,
    };
  }

  private reportHasUnsavedChanges() {
    const originalBookmark = serializeBookmark(this.state.originalBookmark);
    const editedBookmark = serializeBookmark(this.state.editedBookmark);
    return (
      !originalBookmark ||
      originalBookmark.name !== editedBookmark.name ||
      originalBookmark.description !== editedBookmark.description ||
      originalBookmark.type !== editedBookmark.type ||
      this.state.reportManager.hasUnsavedBookmarkParams(originalBookmark.params, editedBookmark.params)
    );
  }

  reportHeaderProps(): ReportHeaderProps {
    return {
      project: this.currentProjectContext,
      results: this.state.results,
      dataGroups: Object.values(dataGroupStore.value),
      bookmark: {...this.state.originalBookmark, ...this.bookmark},
      showCustomAlertModal: this.state.showCustomAlertModal,
    };
  }

  /**
   * @returns true if `create-alert` is in query params. Does not change query params.
   */
  private hasCreateAlertIntent() {
    return new URL(getLocation().href).searchParams.has(`create-alert`);
  }

  /**
   * @returns true if `create-alert` or `anomaly_detection_alert` query param is found in the URL. Clears the param as well.
   * We pass this down to query-report-header so it can open the alert dialog, if requested via URL.
   */
  private checkForCreateAlertIntent() {
    const currentUrl = new URL(getLocation().href);
    if (currentUrl.searchParams.has(`create-alert`) || currentUrl.searchParams.has(`anomaly_detection_alert`)) {
      const mpSource = currentUrl.searchParams.get(`mp_source`);
      emitTrackEvent(this, {
        eventName: `[Custom Alerts] Followed create alert CTA`,
        props: {
          tab: this.getReportName(),
          'Bookmark ID': this.state.originalBookmark?.id,
          'arrived from (mp_source)': mpSource,
        },
      });
      currentUrl.searchParams.delete(mpSource);
      window.history.replaceState(null, null, currentUrl);
      return true;
    }
    return false;
  }

  // This event helps trigger workflow tracking as well as help understand time to first query
  emitQueryTrackEvent(reusingPromise: boolean) {
    emitInfraTrackEvent(this, {
      eventName: QueryStartEvent,
      props: {
        queryCount: ++this.queryCount,
        reusingPromise,
        reportEditor: true,
        bookmarkType: this.state.reportManager.bookmarkType,
      },
    });
  }

  async query(queryReason: QueryReason, useQueryCache = true) {
    const {hash, search, pathname} = getLocation();
    const location = {
      state: ``,
      key: ``,
      pathname,
      search,
      hash,
    };
    // shallow copy so that we track the current state of bookmark params
    const bookmarkParams = this.bookmark.params;

    this.update({results: null, error: null, hasReportHitUxSegmentationLimit: false});
    if (!this.state.reportManager.isQueryable(bookmarkParams)) {
      // Set loading to false to handle adding new reports
      this.setLoading(LoadingResource.Result, false);
      return;
    }

    this.emitQueryTrackEvent(false);

    spans.getOrCreateSpan({id: `report-query`});

    this.setLoading(LoadingResource.Result, true);
    this.update({showQuerySamplingCTA: false, showMpTips: false});
    const qtsCtaTimeout = setTimeout(() => {
      if (this.currentProjectContext?.hasFeatureFlag(ProjectFeatureFlag.QTS) && !this.state.querySamplingEnabled) {
        this.update({showQuerySamplingCTA: true, triggerQuerySamplingTour: true});
      }
    }, ShowQuerySamplingCTADelay);

    const mpTipsTimeout = setTimeout(() => {
      const showMpTips =
        !this.currentProjectContext?.hasFeatureFlag(ProjectFeatureFlag.QTS) || this.state.querySamplingEnabled;
      this.update({showMpTips});
    }, SHOW_MIXPANEL_TIPS_DELAY);

    spans.withSpanById(`report-query`, () => {
      this.internalResultsPromise = this.state.reportManager.query({
        bookmarkParams,
        queryReason,
        hasUnsavedBookmarkChanges: this.state.originalBookmark
          ? this.state.reportManager.hasUnsavedBookmarkParams(this.state.originalBookmark.params, bookmarkParams)
          : true,
        bookmarkId: this.state.originalBookmark?.id,
        useQueryCache,
        currentProject: this.currentProjectContext,
        useQuerySampling: this.state.querySamplingEnabled,
        location,
      });
    });

    const results = await this.internalResultsPromise;

    spans.endSpanById(`report-query`);

    clearTimeout(qtsCtaTimeout);
    clearTimeout(mpTipsTimeout);

    // query was aborted, stop processing
    if (results === null) {
      return;
    }

    const isSegmentationLimitHit: boolean =
      (results as BaseQueryResultsWithMetaType)?.meta?.is_segmentation_limit_hit ?? false;
    this.trackQuery({
      eventName: ReportTrackingEvent.Query,
      params: bookmarkParams,
      errorMessage: isErrorResult(results) ? results.error : null,
      error: isErrorResult(results) ? results : null,
      props: {'Is Refresh': !useQueryCache, 'Hit Segmentation Limit': isSegmentationLimitHit},
    });

    // ensure display name data is synced before we display results.
    // TODO: asynchronously update display names in the components that consume them.
    await this.syncDisplayNames();

    updateFavicon({useBadge: true});
    this.setLoading(LoadingResource.Result, false);
    this.handleResults(results, useQueryCache);
  }

  handleResults(results: BaseQueryResultsType | BaseQueryErrorType, useQueryCache: boolean) {
    this.updateReportSamplingFactor(results as BaseQueryResultsWithMetaType);
    if (isErrorResult(results)) {
      this.update({error: results});
    } else {
      const {params, results: transformedResults} = this.state.reportManager.handleResults({
        params: this.state.editedBookmark.params,
        results,
      });
      this.getContext(`queryContextManager`).updateSegmentationMetadatas(
        this.state.reportManager.getSegmentationMetadatas(results),
      );

      const hasReportHitUxSegmentationLimit: boolean =
        (results as BaseQueryResultsWithMetaType)?.meta?.is_segmentation_limit_hit ?? false;
      const uxSegmentationLimit: number = this.state.reportManager.getUxSegmentationLimit(params);
      const maxExportCsvLimit: number = this.state.reportManager.getCsvExportLimit({
        bookmarkParams: params,
        currentProject: this.currentProjectContext,
      });

      this.update({
        results: transformedResults,
        hasReportHitUxSegmentationLimit,
        uxSegmentationLimit,
        maxExportCsvLimit,
      });
      if (!this.reportHasUnsavedChanges()) {
        this.setResultsInDashboardCallback?.(transformedResults, useQueryCache);
      }
    }
    this.update({showCustomAlertModal: this.checkForCreateAlertIntent()});
  }

  handleChartCustomizationUpdate(
    updateType: ChartCustomizationUpdateType,
    update: Partial<ChartCustomizationUpdate>,
    diffSetting: Partial<BookmarkYAxisOptions>,
    oldSetting: BookmarkYAxisOptions,
  ) {
    const bookmarkUpdates = bookmarkUpdatesFromChartCustomization({
      originalBookmarkParams: this.state.editedBookmark.params,
      update,
    });

    this.handleBookmarkUpdate({
      params: bookmarkUpdates,
    });

    const trackingProps = getChartCustomizationUpdateTrackingProps(updateType, diffSetting, oldSetting);
    mixpanel.track(ReportTrackingEvent.UpdateCustomization, trackingProps);
  }

  /**
   * Handles UI updates resulting in updated bookmark params
   */
  handleBookmarkUpdate({params, savedBehaviorId, queryReason, requery, hasChangedChartType}: BookmarkUpdateDetail) {
    const [updatedParams] = this.historyManager.applyUpdates<TODO>({
      source: this.state.editedBookmark.params,
      updates: params,
      metadata: {requery, hasChangedChartType},
    });
    const chartType = this.state.reportManager.getCommonChartType(params); // params.chart_type ?? params.displayOptions?.chartType; // TODO consolidate
    if (hasChangedChartType && this.state.reportManager.reportSwitchInfoForChartType(chartType)?.report) {
      this.switchReport(this.state.reportManager.reportSwitchInfoForChartType(chartType));
      return;
    }
    this.updateBookmarkParams({
      params: updatedParams,
      queryReason,
      requery,
      savedBehaviorId,
      hasChangedChartType,
    });
  }

  /**
   * Makes sure the reportBookmark context has the latest bookmark params
   */
  syncReportBookmarkStore(bookmark?: DisplayBookmark<InsightsBookmarkParams>) {
    bookmark = bookmark ?? this.state.editedBookmark;
    if (!bookmark) {
      return null;
    }

    return this.reportBookmark.syncBookmark(bookmark);
  }

  /**
   * Handles UI actions from the reportBookmark context
   */
  reportBookmarkStoreApplyFn: ApplyParamsFunction = (params, options) => {
    this.handleBookmarkUpdate({
      params,
      queryReason: QueryReason.QbOtherUpdate,
      requery: options.requery ?? true,
      hasChangedChartType: false,
    });
  };

  updateBookmarkParams({
    params,
    newReportManager,
    queryReason,
    requery,
    hasChangedChartType,
    isReportSwitch,
    savedBehaviorId,
  }: {
    params: TODO;
    newReportManager?: ReportManager;
    queryReason: QueryReason;
    requery: boolean;
    hasChangedChartType: boolean;
    isReportSwitch?: boolean;
    savedBehaviorId?: number;
  }) {
    this.update((draft) => {
      draft.reportManager = newReportManager ?? draft.reportManager;
      draft.editedBookmark = {
        params,
        type: (newReportManager?.insightsReportMode || newReportManager?.bookmarkType) ?? draft.editedBookmark.type,
        name: newReportManager ? null : draft.editedBookmark.name,
        description: newReportManager ? null : draft.editedBookmark.description,
      };
      draft.error = requery ? null : draft.error;
      return draft;
    });

    if (hasChangedChartType) {
      this.resetReportLayout();
    }

    if (isReportSwitch) {
      this.dashboardCardId = null;
    }
    this.dispatchUpdate({
      isReportSwitch,
      ...(savedBehaviorId ? {savedBehaviorId} : {}),
    });
    if (requery) {
      this.query(queryReason);
    }
  }

  async fetchBookmarkURL(bookmarkURLSlug?: string): Promise<BookmarkURLResponse> {
    this.update({bookmarkURLSlug, renderStage: 0});

    const requestParams = {
      slug: bookmarkURLSlug,
      projectId: this.projectId,
    };

    try {
      const response = await bookmarkUrlsClient.fetchBookmarkUrl(requestParams);
      return response;
    } catch (err) {
      handleFetchBookmarkURLError(err, {...requestParams, reportName: this.getReportName()});
      return null;
    } finally {
      this.helpers.incrRenderStage();
    }
  }

  get bookmarkType(): BookmarkType {
    return this.state.editedBookmark?.type;
  }

  getReportName(): ReportName {
    return this.bookmarkType ? BookmarkTypeToReportName[this.bookmarkType] : null;
  }

  queryBuilderEventHandlers: ReportQueryBuilderEventHandlers = {
    update: (ev) => this.handleBookmarkUpdate(ev.detail),
    reportswitch: (ev) => this.handleReportSwitch(ev),
  };

  visualizationEventHandlers: VisualizationEventHandlers = {
    update: (ev) => this.handleBookmarkUpdate(ev.detail),
    'export-csv': () => this.handleExport(DownloadFormat.CSV),
    'request-oda': (ev) => this.handleOdaRequest(ev),
    'toggle-query-sampling': (ev) => this.handleQuerySamplingToggled(ev.detail.on),
    reportswitch: (ev) => this.handleReportSwitch(ev),
  };

  visualizationAttrs(): ReportVisualizationAttrs {
    const status = this.getReportStatus();
    return {
      ...(status ? {status} : null),
      'is-download-mode': this.isDownloadMode(),
      'has-data-table': this.state.reportManager.hasDataTable(this.state.editedBookmark.params),
      'show-resizing-layout': true,
      'workspace-id': this.workspaceId,
      'project-id': this.projectId,
      'show-query-sampling-cta': this.state.showQuerySamplingCTA,
      'show-mp-tips': this.state.showMpTips,
      'insights-report-mode': this.state.reportManager.insightsReportMode,
      ...(!this.reportHasUnsavedChanges() && this.state.originalBookmark?.id
        ? {'rca-source-id': this.state.originalBookmark.id}
        : {}),
    };
  }

  visualizationProps(): ReportVisualizationProps<any> {
    return {
      paramsAndResults: {
        params: this.bookmark.params,
        results: this.state.results,
      },
      error: this.state.error,
      project: this.currentProjectContext,
      dataGroups: Object.values(dataGroupStore.value),
      customEvents: Object.values(customEventsStore.value),
      userDetails: userDetailsStore.value,
    };
  }

  handleBookmarkSaveOnHeaderChange(ev) {
    const editedBookmark = this.bookmark;

    const {name, description, dashboard, saveAsNew} = ev.detail;
    let [newEditedBookmark] = this.historyManager.applyUpdates<DisplayBookmark>({
      source: editedBookmark,
      updates: {...editedBookmark, name, description},
      metadata: {key: `header`},
    });
    this.update({editedBookmark: newEditedBookmark});

    let shouldIncludePromise = true;
    if (this.state.querySamplingEnabled) {
      shouldIncludePromise = false;
    } else {
      // we shouldn't pass up a promise if any of the applied overrides were changed/removed in the editor
      const paramsWithOverrides = this.getBookmarkParamsWithOverrides(editedBookmark.params, editedBookmark.type);
      shouldIncludePromise = !this.state.reportManager.hasUnsavedBookmarkParams(
        editedBookmark.params,
        paramsWithOverrides,
      );
    }

    newEditedBookmark = {
      ...newEditedBookmark,
      params: this.state.reportManager.getBookmarkForSaving(newEditedBookmark.params),
    };

    const trackingProps = this.trackQuery({
      eventName: ReportTrackingEvent.Save,
      params: newEditedBookmark.params,
      bookmarkName: newEditedBookmark.name,
    });

    const saveEvent: SaveEvent = new CustomEvent(`save`, {
      detail: {
        editedBookmark: serializeBookmark(newEditedBookmark),
        hydratedBookmark: newEditedBookmark,
        originalBookmark: this.state.originalBookmark,
        queryPromise: shouldIncludePromise ? this.internalResultsPromise : null,
        dashboard,
        originDashboard: this.state.originDashboard,
        saveAsNew,
        trackingProps,
        ...(!this.attr(`app-mode`)
          ? {
              dashboardCardId: this.dashboardCardId,
              updateBookmarkUrlOnFasteditSave: this.updateBookmarkUrlOnFasteditSave,
            }
          : {}),
      },
    });
    this.dispatchEvent(saveEvent);
  }

  handleBookmarkDelete() {
    this.state.reportManager.track(ReportTrackingEvent.Delete, {
      ...this.getContext(`reportLayoutManager`).getTrackingProps(),
      'Bookmark ID': this.state.originalBookmark?.id,
    });

    const deleteEvent: DeleteEvent = new CustomEvent(`delete`, {
      detail: {
        dashboardCardId: this.dashboardCardId,
        originalBookmark: this.state.originalBookmark,
      },
    });
    this.dispatchEvent(deleteEvent);
  }

  handleRefresh() {
    customEventsStore.refresh();
    customPropertiesStore.refresh({
      projectId: this.projectId,
      workspaceId: this.workspaceId,
    });
    lookupTablesStore.refresh({
      projectId: this.projectId,
      workspaceId: this.workspaceId,
    });
    eventDefinitionsStore.refresh();
    userDetailsStore.refresh();
    propertyDefinitionsForEventsStore.refresh();
    peoplePropertyDefinitionsStore.refresh();
    this.query(QueryReason.QbOtherUpdate, false);
  }

  async handleCsvExport({format, limit}: {format: DownloadFormat; limit: number}) {
    const {search, pathname, hash} = getLocation();
    const {result, filename, isSegmentationLimitHit} = await this.state.reportManager.csvExportQuery({
      limit,
      bookmark: this.state.editedBookmark,
      currentProject: this.currentProjectContext,
      location: {
        search,
        pathname,
        hash,
        key: ``,
        state: ``,
      },
    });
    if (format === DownloadFormat.CSV) {
      commonReportUtils.downloadLocalCSVData(filename, result);
    } else if (format === DownloadFormat.CSVCLIPBOARD) {
      try {
        await copy(result);
        addToast({
          type: ToastType.SUCCESS,
          message: `CSV copied to clipboard`,
        });
      } catch (err) {
        reporter.error(err);
        addToast({
          message: `There was an error copying the CSV data to your clipboard.`,
          type: ToastType.ERROR,
        });
      }
    } else {
      reporter.error(`Unexpected format for CSV export: ${format}`);
    }
    return {isSegmentationLimitHit};
  }

  private updateReportSamplingFactor(results: BaseQueryResultsWithMetaType) {
    let reportSamplingFactor = 1;
    if (`meta` in results && `min_sampling_factor` in results.meta) {
      reportSamplingFactor = results.meta.min_sampling_factor;
    } else if (`metadata` in results && `min_sampling_factor` in results.metadata) {
      reportSamplingFactor = results.metadata.min_sampling_factor;
    }
    if (this.state.reportSamplingFactor !== reportSamplingFactor) {
      this.update({reportSamplingFactor});
    }
  }

  private async handleScreenshotExport(format: DownloadFormat) {
    const params: DownloadReportRequest = {
      format,
      type: this.attr(`app-mode`) ? this.state.reportManager.bookmarkType : ReportName.Dashboards,
      report_hash: getLocation().hash,
    };

    if (this.reportHasData()) {
      const endpoint = queryApiUrl(this.state.reportManager.queryManager.endpoint);
      params.report_cache = JSON.stringify({[endpoint]: JSON.stringify(this.state.results)});
    }

    const blob = await makeDownloadQuery(this.projectId, this.workspaceId, params);
    downloadBlobAsFile(blob, this.state.editedBookmark.name || `Untitled report`, format);
  }

  async handleExport(format: DownloadFormat) {
    let startToast: Toast;

    const addStartToast = (): Toast => {
      let formatStr: string = format;
      if (format === DownloadFormat.CSVCLIPBOARD) {
        formatStr += ` to Clipboard`;
      }
      return addToast({
        message: `Exporting ${formatStr}...`,
        timeout: 0,
      });
    };

    // shallow copy so that we track the current state of bookmark params
    const bookmarkParams = {...this.state.editedBookmark.params};

    let errorMessage = null;
    try {
      if (isScreenshotDownloadFormat(format)) {
        startToast = addStartToast();
        await this.handleScreenshotExport(format);
      } else if (format === DownloadFormat.CSV || format === DownloadFormat.CSVCLIPBOARD) {
        const csvExportSpec = await this.state.reportManager.getCsvExportSpec({
          bookmarkParams: this.state.editedBookmark.params,
          currentProject: this.currentProjectContext,
        });
        if (csvExportSpec.action === CsvExportAction.Cancel) {
          return;
        }

        startToast = addStartToast();
        const {isSegmentationLimitHit} = await this.handleCsvExport({format, limit: csvExportSpec.exportLimit});
        if (typeof csvExportSpec.maxCustomLimit === `number` && isSegmentationLimitHit) {
          addToast({
            type: ToastType.INFO,
            message: `Export results exceeded the limit you selected. You can export up to ${abbreviateNumber(
              csvExportSpec.maxCustomLimit,
              {trimZeroes: true},
            )} rows`,
          });
        }
      }

      addToast(DOWNLOAD_SUCCESS_TOAST);
    } catch (err) {
      reporter.error(err);
      errorMessage = err.message;
      addToast(DOWNLOAD_FAIL_TOAST);
    } finally {
      setTimeout(() => closeToast(startToast), DOWNLOAD_TOAST_OVERLAP_MS);
      this.trackQuery({
        eventName: ReportTrackingEvent.Export,
        params: bookmarkParams,
        errorMessage,
        props: {format},
      });
    }
  }

  async handleOdaRequest(ev: RequestOdaEvent): Promise<void> {
    const hasUnsavedChanges = this.state.originalBookmark
      ? this.state.reportManager.hasUnsavedBookmarkParams(
          this.state.originalBookmark.params,
          this.state.editedBookmark.params,
        )
      : true;

    const bookmarkType = this.state.reportManager.insightsReportMode;
    const taskId = `${bookmarkType}-${this.userId}-${new Date().toISOString()}`;

    let errorMessage = null;
    try {
      await submitOdaRequest({
        taskId,
        projectId: this.projectId,
        workspaceId: this.workspaceId,
        userId: this.userId,
        projectAsOfDate: this.currentProjectContext?.projectAsOfDate,
        bookmarkParams: this.state.editedBookmark.params,
        bookmarkId: this.state.originalBookmark?.id,
        hasUnsavedChanges,
        bookmarkType,
        bookmarkDateRange: ev.detail.bookmarkDateRange,
        bookmarkUrl: ev.detail.bookmarkUrl,
      });

      addToast({message: `Analysis scheduled.`});
    } catch (err) {
      reporter.error(err);
      errorMessage = err.message;
      addToast({message: `There was an error scheduling your analysis.`});
    } finally {
      this.trackQuery({
        eventName: ReportTrackingEvent.OdaSubmitted,
        params: this.state.editedBookmark.params,
        errorMessage,
        props: {taskId},
      });
    }
  }

  getReportStatus(): ReportStatus {
    if (this.state.loading[LoadingResource.Result]) {
      return ReportStatus.Loading;
    } else if (
      !this.state.reportManager.isQueryable(this.state.editedBookmark.params) &&
      !this.state.reportManager.queryManager.hasQueried()
    ) {
      return ReportStatus.Initial;
    } else if (
      !this.state.reportManager.isQueryable(this.state.editedBookmark.params) &&
      this.state.reportManager.queryManager.hasQueried()
    ) {
      return ReportStatus.Empty;
    } else if (this.state.reportManager.isEmptyResults(this.state.results, this.state.editedBookmark.params)) {
      return ReportStatus.NoData;
    } else if (this.state.error) {
      return ReportStatus.Error;
    }

    return null;
  }

  updateBookmarkUrlOnFasteditSave = ({
    originalBookmark,
    dashboardCardId,
  }: {
    originalBookmark: Bookmark;
    dashboardCardId: DashboardCardId;
  }) => {
    this.dashboardCardId = dashboardCardId;
    this.update({originalBookmark});
    this.updateBookmarkUrl();
    return this.state.bookmarkURLSlug;
  };

  shouldGenerateBookmarkUrl() {
    // Guard against a POST when in Embed Mode since we don't want create new URL and
    // also want to constrain amount of POST calls allowed when embedding
    return !isEmbedMode();
  }

  updateBookmarkUrl() {
    const originDashboard = this.state.originDashboard;
    const querySamplingEnabled = this.state.querySamplingEnabled;
    const overrides = {
      ...(originDashboard ? {originDashboard} : {}),
      ...(!isNil(querySamplingEnabled) ? {querySamplingEnabled} : {}),
      ...(this.dashboardCardId ? {dashboardCardId: this.dashboardCardId} : {}),
    };

    const {params, name, description} = serializeBookmark(this.bookmark);

    if (this.shouldGenerateBookmarkUrl()) {
      const bookmarkURLSlug = bookmarkUrlsClient.getBookmarkUrl({
        project_id: this.projectId,
        type: this.getReportName(),
        bookmark_id: this.state.originalBookmark?.id,
        // NOTE: bookmark-url slugs won't be used when viewing a bookmark without unsaved changes (which will still use
        // the `/#report/<report_id>/` url scheme). bookmark-url slug will only be used when changes are made to the
        // bookmark. Thus it's safe to include params/name/description without worrying that they'll become stale if
        // the bookmark is updated.
        params,
        name,
        description,
        ...(isEmpty(overrides) ? {} : {overrides}),
      });

      this.update({bookmarkURLSlug});
    }
  }

  dispatchUpdate({
    isReportSwitch,
    savedBehaviorId,
    updatePromise,
  }: {
    isReportSwitch?: boolean;
    savedBehaviorId?: number;
    updatePromise?: Promise<BookmarkURLResponse | Bookmark>;
  } = {}) {
    this.updateBookmarkUrl();

    if (isReportSwitch) {
      this.trackFasteditVisitedReport();
    }

    const event: UpdateEvent = new CustomEvent(`update`, {
      detail: {
        bookmarkURLSlug: this.state.bookmarkURLSlug,
        updatePromise,
        bookmark: this.state.editedBookmark,
        dashboardCardId: this.dashboardCardId,
        isReportSwitch,
        savedBehaviorId,
      },
    });
    this.updateQueryContext();
    this.dispatchEvent(event);
  }

  reportHeaderEventHandlers: ReportHeaderEventHandlers = {
    'back-to-board': async (ev) => {
      ev.stopPropagation();

      const backToBoard = await this.confirmBackToBoard();
      if (!backToBoard) {
        // close button is a href with dashboard url, so need to prevent navigation to dashboard.
        return;
      }

      this.dispatchEvent(
        new CustomEvent(`back-to-board`, {
          detail: {
            source: BackToBoardSource.BackButton,
          } satisfies BackToBoardDetail,
        }),
      );
    },
    refresh: () => this.handleRefresh(),
    save: (ev) => this.handleBookmarkSaveOnHeaderChange(ev),
    delete: () => this.handleBookmarkDelete(),
    export: (ev) => this.handleExport(ev.detail.format),
    update: (ev) => {
      const [editedBookmark] = this.historyManager.applyUpdates<DisplayBookmark>({
        source: this.state.editedBookmark,
        updates: {...this.state.editedBookmark, ...ev.detail},
        metadata: {key: `header`},
      });
      this.update({editedBookmark});
      this.dispatchUpdate();
    },
    move: (ev) => {
      const moveContentAction = {
        actionType: `move`,
        contentId: this.state.originalBookmark?.id,
        contentParams: {
          target_dashboard_id: ev.detail.value,
        },
        contentType: `report`,
      };
      this.dispatchEvent(new CustomEvent(`move`, {detail: moveContentAction}));
    },
    'toggle-query-sampling': (ev) => this.handleQuerySamplingToggled(ev.detail.on),
    'dismiss-query-sampling-tour': () => this.update({triggerQuerySamplingTour: false}),
  };

  handleQuerySamplingToggled(enabled: boolean) {
    this.update({querySamplingEnabled: enabled});
    if (this.state.reportManager) {
      this.query(QueryReason.QbQTSUpdate);
      this.dispatchUpdate();
    }
  }

  handleReportSwitch(ev: ReportSwitchEvent) {
    if (this.attr(`app-mode`)) {
      this.getContext(`queryContextManager`).updateQuery(
        this.getReportQueryContext({additionalContext: ev.detail.additionalContext}),
      );
      // allow this to propagate to the document, where spa-nav-manager will handle the report switch.
      return;
    }
    ev.stopPropagation();
    this.switchReport(ev.detail);
  }

  switchReport({report, additionalContext}: Partial<ReportSwitchEvent[`detail`]>) {
    if (!SUPPORTED_BOOKMARK_TYPES.includes(report)) {
      // this should never happen, but handle it gracefully
      reporter.error(`ReportEditor: unsupported report switch: ${report}`);
      return;
    }

    this.state.reportManager.abortPendingQuery();
    this.update({results: null, error: null});

    const newReportManager = this.createReportManager({bookmarkType: report, insightsReportMode: report});
    const queryContext = this.getReportQueryContext({additionalContext});

    let params;
    if (queryContext) {
      params = newReportManager.getBookmarkParamsFromQueryContext(queryContext, {
        project: this.currentProjectContext,
        customEvents: Object.values(customEventsStore.value),
      });
    } else {
      params = newReportManager.getDefaultBookmarkParams(this.currentProjectContext);
    }

    this.update({originalBookmark: null});
    this.updateBookmarkParams({
      newReportManager,
      queryReason: QueryReason.NavFromOtherReport,
      params,
      requery: true,
      hasChangedChartType: true,
      isReportSwitch: true,
    });
  }

  getReportQueryContext(
    {additionalContext}: {additionalContext: AdditionalContext} = {additionalContext: null},
  ): QueryContext {
    const customEvents = Object.values(customEventsStore.value);
    const dataGroups = Object.values(dataGroupStore.value);
    return this.state.reportManager.getQueryContextFromBookmarkParams({
      additionalContext,
      bookmarkType: this.state.editedBookmark.type,
      customEvents,
      dataGroups,
      metricsResource: this.getContext(`metrics`),
      originDashboard: this.state.originDashboard,
      params: this.bookmark?.params ?? this.state.editedBookmark?.params,
      project: this.currentProjectContext,
      querySamplingEnabled: this.state.querySamplingEnabled,
      behaviorsResource: this.getContext(`behaviors`),
    });
  }

  getReportMeta(): ReportMeta {
    const editedBookmark = this.state.editedBookmark;
    const subtitles = editedBookmark?.description ? [editedBookmark?.description] : [];
    const hasUnsavedChanges = this.reportHasUnsavedChanges();

    return {
      reportName: this.state.reportManager.reportDisplayName,
      analysisType: this.state.reportManager.getAnalysisDescription(editedBookmark.params),
      title: editedBookmark.name,
      dateRange: this.state.reportManager.getDateRange(editedBookmark.params),
      subtitles,
      isEmpty: !!this.state.error || !this.state.reportManager.isQueryable(editedBookmark.params),
      hasUnsavedChanges,
      bookmarkType: this.state.reportManager.bookmarkType,
    };
  }

  isDownloadMode(): boolean {
    return this.getDownloadMode() !== DownloadMode.None;
  }

  getDownloadMode() {
    return getDownloadMode(this.getContext(`urlManager`));
  }

  resetReportLayout() {
    this.getContext(`reportLayoutManager`).resetReportLayoutIfNeeded({
      hasDataTable: this.state.reportManager.hasDataTable(this.state.editedBookmark.params),
    });
  }

  canUseChartCustomization() {
    return isChartCustomizationEnabled(this.currentProjectContext);
  }
}

export const ReportEditorEl = defineAndMakeJsxFactory<ReportEditorJsxProps>(`mp-report-editor`, ReportEditor);
export const ReportEditorReact = panel2react<ReportEditorJsxProps>(`mp-report-editor`, ReportEditor);
